/**
 * Suggested Rules Index Page
 *
 * Main page for reviewing AI-suggested classification rules.
 * Shows pending rules and allows users to approve or reject them.
 *
 * Features:
 * - List of pending suggested rules
 * - Approve/reject actions with toast notifications
 * - Empty state when no pending rules
 * - Loading skeleton during data fetch
 * - Pagination for large result sets
 * - Link back to transactions page
 */

import { useEffect, useCallback } from 'react';
import { Head, Link, usePage } from '@inertiajs/react';
import { Toaster, toast } from 'sonner';
import { Sparkles, ArrowLeft, Loader2 } from 'lucide-react';
import { Navbar } from '@/components';

import { useSuggestedRules } from '@/hooks/useSuggestedRules';
import { SuggestedRuleCard } from './components/SuggestedRuleCard';
import { Pagination } from '../shared';
import type { SuggestedRulesPageProps } from '@/types/accounting';

/**
 * SuggestedRulesIndex is the main page for reviewing suggested rules.
 */
export default function SuggestedRulesIndex() {
  // Get page props from Inertia
  const { accounts = [] } = usePage<SuggestedRulesPageProps>().props;

  const {
    suggestedRules,
    pagination,
    loading,
    error,
    fetchRules,
    approveRule,
    rejectRule,
  } = useSuggestedRules();

  // Fetch rules on mount
  useEffect(() => {
    fetchRules().catch(() => {
      // Error is handled by the hook
    });
  }, [fetchRules]);

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      fetchRules(page).catch(() => {
        // Error is handled by the hook
      });
    },
    [fetchRules]
  );

  // Handle approve action
  const handleApprove = useCallback(
    async (
      id: number,
      overrides?: {
        name?: string;
        patternOverride?: string;
        targetAccountIdOverride?: number;
      }
    ) => {
      try {
        const result = await approveRule(id, overrides);
        toast.success(
          `Rule approved${result.classificationRule.name ? `: ${result.classificationRule.name}` : ''}`
        );
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to approve rule');
        throw err;
      }
    },
    [approveRule]
  );

  // Handle reject action
  const handleReject = useCallback(
    async (id: number) => {
      try {
        await rejectRule(id);
        toast.success('Rule rejected');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to reject rule');
        throw err;
      }
    },
    [rejectRule]
  );

  return (
    <>
      <Head title="Review Suggested Rules" />
      <Toaster position="top-right" richColors closeButton />

      <div className="min-h-screen bg-gray-50" data-testid="suggested-rules-page">
        <Navbar currentSection="accounting" />

        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                href="/accounting/transactions"
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={16} />
                Transactions
              </Link>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                  <Sparkles className="text-purple-500" size={24} />
                  Review Suggested Rules
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  AI-generated classification rules awaiting your approval
                </p>
              </div>
            </div>
            <nav className="flex items-center gap-2">
              <Link
                href="/accounting/classification"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Metrics
              </Link>
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-6 py-6 max-w-4xl mx-auto">
          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-red-800">Error Loading Rules</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => fetchRules()}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && suggestedRules.length === 0 && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse"
                >
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="h-4 bg-gray-200 rounded w-24" />
                  </div>
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="h-10 bg-gray-100 rounded" />
                  </div>
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="h-4 bg-gray-100 rounded w-48" />
                  </div>
                  <div className="px-4 py-3 border-b border-gray-100 bg-purple-50/50">
                    <div className="h-16 bg-purple-100 rounded" />
                  </div>
                  <div className="px-4 py-3">
                    <div className="flex gap-2">
                      <div className="flex-1 h-10 bg-gray-200 rounded" />
                      <div className="flex-1 h-10 bg-gray-200 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && suggestedRules.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-16 text-center">
                <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  No Suggested Rules
                </h2>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                  There are no pending AI-suggested rules to review.
                  Rules will appear here when the AI identifies patterns in your transactions.
                </p>
                <Link
                  href="/accounting/transactions"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <ArrowLeft size={16} />
                  Back to Transactions
                </Link>
              </div>
            </div>
          )}

          {/* Rules List */}
          {!loading && suggestedRules.length > 0 && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {pagination?.totalCount || suggestedRules.length} pending rule
                  {(pagination?.totalCount || suggestedRules.length) !== 1 ? 's' : ''} to review
                </p>
                {loading && (
                  <Loader2 size={16} className="animate-spin text-gray-400" />
                )}
              </div>

              <div className="space-y-4" data-testid="suggested-rules-list">
                {suggestedRules.map((rule) => (
                  <SuggestedRuleCard
                    key={rule.id}
                    rule={rule}
                    accounts={accounts}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    disabled={loading}
                  />
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-6 flex justify-center">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    siblingCount={1}
                    showFirstLast
                  />
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
